import { formatDisplayDate, formatTime, calculateDuration } from '../../utils/dateUtils';
import React, { useEffect, useState } from 'react';
import { db } from '../../utils/firebase';
import { startOrGetConversation } from '../../utils/chat';
import ChatPanel from '../chat/ChatPanel';

import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  addDoc,
  getDoc,
  setDoc,
  deleteDoc, 
  serverTimestamp, 
  arrayUnion
} from 'firebase/firestore';


const MatchesView = ({
  onViewDetails,
  onViewProfile,
  onCancelMatch,
  onFeedbackRequest,
  user,
  trails,
  profiles: userProfiles // ✅ renamed here
}) => {


  const [matchStatus, setMatchStatus] = useState('confirmed');
  const [userMatches, setUserMatches] = useState([]);
  const [otherProfileName, setOtherProfileName]  = useState(null);
  const trailsMap = Object.fromEntries(trails.map(t => [t.id, t]));
  const profilesMap = Object.fromEntries(userProfiles.map(p => [p.id, p]));
  // ✅ Chat modal state + open handler
  const [chat, setChat] = React.useState({ open: false, id: null });

async function handleMessage(otherProfile, match) {
  setOtherProfileName(otherProfile.fullName);
  // ✅ Explain why it won't open when there's no partner yet
  if (!otherProfile?.id) {
    alert('You can message only after the match is confirmed with a partner.');
    return;
  }
  if (!user?.uid) {
    alert('Please sign in to send messages.');
    return;
  }

  const uid1 = user.uid;
  const uid2 = otherProfile.id;
  const fallbackId = [uid1, uid2].sort().join('__') + (match?.id ? `__${match.id}` : '');

  try {
    const conversationId = await startOrGetConversation({
      uid1,
      uid2,
      matchId: match?.id,
      trailId: match?.trail_id
    });
    setChat({ open: true, id: conversationId });
  } catch (err) {
    console.error('startOrGetConversation failed:', err);
    // open the modal anyway so you see the error banner in ChatPanel
    setChat({ open: true, id: fallbackId });
  }
}


  useEffect(() => {
    if (!user?.uid) return;
 
    const matchesRef = collection(db, 'matches');
 
    const q1 = query(matchesRef, where('profile_ids.id1', '==', user.uid));
    const q2 = query(matchesRef, where('profile_ids.id2', '==', user.uid));
    const q3 = query(matchesRef, where('participants', 'array-contains', user.uid));
    const q4 = query(
      matchesRef,
      where('status', '==', 'searching'),
      where('participants', 'array-contains', user.uid)
    );
   
    const handleSnapshot = (snapshot) => {
      const updated = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
 
      setUserMatches(prev => {
        const combined = [...prev, ...updated];
        const deduped = Array.from(new Map(
          combined.map(m => {
            const key = m.status === 'confirmed'
              ? `${m.trail_id}_${m.start_time?.seconds}_${(m.participants || []).sort().join('_')}`
              : m.id;
            return [key, m];
          })
        ).values());
 
        return deduped;
      });
    };
 
    const unsub1 = onSnapshot(q1, handleSnapshot);
    const unsub2 = onSnapshot(q2, handleSnapshot);
    const unsub3 = onSnapshot(q3, handleSnapshot);
    const unsub4 = onSnapshot(q4, handleSnapshot); // ✅ live listener for searching match
 
    return () => {
      unsub1();
      unsub2();
      unsub3();
      unsub4(); // ✅ proper cleanup
    };
  }, [user]);

  const getPartnerProfile = (match, currentUserUid, profilesMap) => {
    if (!match || !match.profile_ids || !currentUserUid || !profilesMap) return null;
    // Find the ID that is NOT the current user's ID
    const partnerId = [match.profile_ids.id1, match.profile_ids.id2].find(id => id && id !== currentUserUid);
    // Return the profile object from the map
    return profilesMap[partnerId] || null;
};
 
  const onAcceptMatch = async (matchId) => {
    try {
      const matchRef = doc(db, 'matches', matchId);
      const matchSnap = await getDoc(matchRef);
      if (!matchSnap.exists()) return;
 
      const matchData = matchSnap.data();
      const id1 = matchData.profile_ids?.id1;
      const existingId2 = matchData.profile_ids?.id2;
 
      if (id1 === user.uid && (!existingId2 || existingId2 === user.uid)) {
        alert("You cannot confirm a match with yourself.");
        return;
      }
 
      const id2 = user.uid === id1 ? existingId2 : user.uid;
      if (id1 === id2) {
        alert("You cannot confirm a match with yourself.");
        return;
      }
 
      const participants = Array.from(new Set([
        matchData.profile_ids?.id1,
        matchData.profile_ids?.id2,
        user.uid,
      ]));
 
      const skillLevels = { beginner: 1, intermediate: 2, advanced: 3 };
      const skill1 = skillLevels[user.skills?.hiking || 'beginner'];
      const skill2 = skillLevels[userProfiles[id1]?.skills?.hiking || 'beginner'];
      const skillDiff = Math.abs(skill1 - skill2);
      const skillScore = (1 - skillDiff / 2) * 100;
 
      const calcPrefScore = (u1, u2) => {
        let score = 0;
        if (u1.preferences?.pace === u2.preferences?.pace) score += 20;
        if (u1.preferences?.communication === u2.preferences?.communication) score += 15;
        if (u1.preferences?.adventureLevel === u2.preferences?.adventureLevel) score += 15;
        return score;
      };
 
      const preferenceScore = calcPrefScore(userProfiles[id1] || {}, user);
      const compatibility = Math.round((skillScore + preferenceScore) / 2);
 
      const confirmedMatch = {
        trail_id: matchData.trail_id,
        status: 'confirmed',
        start_time: matchData.start_time,
        end_time: matchData.end_time,
        profile_ids: { id1, id2 },
        participants,
        compatibility,
        confirmedAt: new Date(),
        fromMatchId: matchData.fromMatchId || null,
      };
 
      const newConfirmedRef = await addDoc(collection(db, 'matches'), confirmedMatch);
 
      await updateDoc(matchRef, {
        participants: participants,
        status: 'confirmed',
        profile_ids: { id1, id2 },
        compatibility,
        confirmedAt: new Date(),
        fromMatchId: matchId,
      });
 
      // 💡 Locally add the confirmed match
      setUserMatches(prev => {
        const withoutOld = prev.filter(m =>
          !(m.trail_id === matchData.trail_id &&
            m.start_time?.seconds === matchData.start_time?.seconds)
        );
        return [...withoutOld, { ...confirmedMatch, id: newConfirmedRef.id }];
      });
 
      // ✅ Create and immediately push new "searching" status locally
      const newSearchId = `${matchData.trail_id}_${user.uid}_${Date.now()}`;
      const searchData = {
        trail_id: matchData.trail_id,
        status: 'searching',
        start_time: matchData.start_time,
        end_time: matchData.end_time,
        profile_ids: {
          id1: user.uid,
          id2: null
        },
        participants: [user.uid],
        createdAt: new Date().toISOString(),
        compatibility: null
      };
 
      await setDoc(doc(db, 'matches', newSearchId), searchData);
 
      // 💡 Optimistic local push
      setUserMatches(prev => {
        const withoutOld = prev.filter(
          m => !(m.trail_id === searchData.trail_id &&
                 m.start_time?.seconds === searchData.start_time?.seconds &&
                 m.status === 'searching' &&
                 m.profile_ids?.id1 === user.uid)
        );
        return [...withoutOld, { ...searchData, id: newSearchId }];
      });
     
 
    } catch (err) {
      console.error("Failed to accept match:", err);
    }
  };
 
 
 
  const handleCancelMatch = async (matchId) => {
    try {
      const matchRef = doc(db, 'matches', matchId);
      const matchSnap = await getDoc(matchRef);
 
      if (!matchSnap.exists()) return;
      const matchData = matchSnap.data();
 
      // Allow cancel only if user is part of the match
      if (
        matchData.profile_ids?.id1 !== user.uid &&
        matchData.profile_ids?.id2 !== user.uid &&
        !matchData.participants?.includes(user.uid)
      ) {
        console.warn("You don't have permission to cancel this match.");
        return;
      }
 
      // 🔄 Update the status to 'cancelled' (don’t delete directly)
      await updateDoc(matchRef, {
        status: 'cancelled',
        cancelReason: 'user_cancelled'
      });
 
      // 🧹 Optimistically remove from UI
      setUserMatches(prev =>
        prev.filter(m => m.id !== matchId)
      );
    } catch (err) {
      console.error("Failed to cancel match:", err);
    }
  };

// const onAcceptMatch = async (matchId) => {
//   try {
//     const matchRef = doc(db, "matches", matchId);
//     const snap = await getDoc(matchRef);
//     if (!snap.exists()) return;

//     const m = snap.data();
//     const { id1, id2 } = m.profile_ids || {};

//     // Only invitee (id2) can accept
//     if (user?.uid !== id2) {
//       alert("Only the invitee can accept this request.");
//       return;
//     }

//     // ✅ Update match → confirmed
//     await updateDoc(matchRef, {
//       status: "confirmed",
//       confirmedAt: serverTimestamp(),
//       updatedAt: serverTimestamp(),
//       participants: arrayUnion(user.uid),
//     });

//     // ✅ Create a conversation doc
//     const convId = [id1, id2].sort().join("_") + `__${matchId}`;
//     const convRef = doc(db, "conversations", convId);

//     await setDoc(convRef, {
//       members: [id1, id2],
//       matchId: matchId,
//       trailId: m.trail_id || null,
//       createdAt: serverTimestamp(),
//     }, { merge: true });  // won't overwrite if it already exists

//   } catch (err) {
//     console.error("Failed to accept match:", err);
//   }
// };
 



  const filteredMatches = userMatches.filter(match => {
    // Ensure match has valid status and involves the user (basic validation)
    if (!match || !match.status || !user || !user.uid || !match.participants?.includes(user.uid)) {
        return false; // Filter out invalid or irrelevant matches
    }

    // Filter logic based on the selected tab
    if (matchStatus === 'pending') {
        // Show 'searching' (user's own initiated search) and 'pending' (received invite)
        return match.status === 'pending' || match.status === 'searching';
    }

    if (matchStatus === 'completed') {
        // Show 'completed' matches
        return match.status === 'completed';
    }

    // Default: matchStatus is 'confirmed', show 'confirmed' matches
    return match.status === 'confirmed';
});


  const uniqueFilteredMatches = filteredMatches.filter(
    (match, index, self) =>
      index === self.findIndex(m =>
        m.id === match.id ||
        (
          m.trail_id === match.trail_id &&
          m.start_time?.seconds === match.start_time?.seconds &&
          m.status === 'confirmed'
        )
      )
  );
 
 


  return (
    <div className="min-h-screen bg-white p-6">
      <header className="mb-6">
        <h1 className="text-3xl font-bold">Your Matches</h1>
      </header>


      <div className="flex bg-gray-100 rounded-full p-1 mb-6">
        {['confirmed', 'pending', 'completed'].map(status => (
          <button
            key={status}
            className={`flex-1 py-2 rounded-full text-sm ${matchStatus === status ? 'bg-white shadow' : ''}`}
            onClick={() => setMatchStatus(status)}
          >
            {status === 'confirmed' ? 'Upcoming' : status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {filteredMatches.map((match, index) => {
          const trail = trailsMap[match.trail_id];
          //const { id1, id2 } = match.profile_ids || {};
          const { id1, id2 } = match.profile_ids || {};
          const isInviter = id1 === user.uid;   // the person who sent the invite
          const isInvitee = id2 === user.uid;   // the person who received the invite
          const otherId = [id1, id2].find(id => id && id !== user.uid);
          const otherProfile = profilesMap[otherId];
          const uniqueKey = `${match.id}-${index}`;
          const estTime = trail?.hours ?? 'N/A';
return (
<div
  key={uniqueKey}
  className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 max-w-xl mx-auto"
>
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
            <div>
              <h3 className="font-semibold text-lg">{trail?.name || 'Loading...'}</h3>
              {otherProfile && (
                <p className="text-sm text-gray-500">with {otherProfile?.name}</p>
              )}
            </div>
            <span className={`text-sm px-3 py-1 rounded-full ${match.status === 'pending'
              ? 'bg-yellow-100 text-yellow-800'
              : match.status === 'searching'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-green-100 text-green-800'
              }`}>
              {match.status.charAt(0).toUpperCase() + match.status.slice(1)}
            </span>
          </div>


  <div className="text-sm text-gray-600 space-y-1 mb-3">
    <div>
      <strong>Date:</strong>{' '}
      {match.start_time?.toDate
        ? formatDisplayDate(match.start_time.toDate())
        : 'Invalid date'}
    </div>
    <div>
      <strong>Time:</strong> {formatTime(match.start_time)} (Est. {estTime}{' '}
      hrs)
    </div>
    <div>
      <strong>Difficulty:</strong> {trail?.difficulty || 'N/A'}
    </div>
    <div>
      <strong>Length:</strong> {trail?.length || 'N/A'}
    </div>
  </div>

   <div className="mt-2 flex flex-col items-center space-y-2">
  {match.status === 'pending' ? (
    <>
      <button
        onClick={() => onViewProfile(otherProfile, true)}
        className="w-56 border-2 border-purple-600 text-purple-600 rounded-full py-2 text-sm font-semibold"
      >
        View Profile
      </button>
      <button
        onClick={() => onViewDetails(trail, match, 'matches')}
        className="w-56 bg-gradient-to-r from-[#6B01D1] to-[#F2787A] text-white rounded-full py-2 text-sm font-semibold"
      >
        View Trail Details
      </button>

      {/* Invitee sees Accept / Reject */}
{(isInvitee || (match.status === 'pending' && match.participants?.includes(user.uid) && id1 !== user.uid)) && (
  <div className="flex gap-2 w-56">
    <button
      onClick={() => handleCancelMatch(match.id)}
      className="flex-1 bg-gray-900 text-white rounded-full py-2 text-sm font-semibold"
    >
      Reject
    </button>
    <button
      onClick={() => onAcceptMatch(match.id)}
      className="flex-1 bg-gradient-to-r from-[#6B01D1] to-[#F2787A] text-white rounded-full py-2 text-sm font-semibold"
    >
      Accept
    </button>
  </div>
)}

      {/* Inviter can withdraw the invite */}
      {isInviter && (
        <button
          onClick={() => handleCancelMatch(match.id)}
          className="w-56 bg-gray-900 text-white rounded-full py-2 text-sm font-semibold"
        >
          Withdraw Invite
        </button>
      )}
    </>
  ) : (
    <>
      <div className="flex flex-wrap justify-center gap-3">
        <button
          onClick={() => onViewProfile(otherProfile, true)}
          className="w-56 border-2 border-purple-600 text-purple-600 rounded-full py-2 text-sm font-semibold"
        >
          View Profile
        </button>
        <button
          onClick={() => onViewDetails(trail, match, 'matches')}
          className="min-w-[140px] w-auto bg-gradient-to-r from-[#6B01D1] to-[#F2787A] text-white rounded-full px-5 py-2 text-sm font-semibold"
        >
          View Details
        </button>
        <button
          onClick={() => handleMessage(otherProfile, match)}
          className="min-w-[120px] w-auto bg-gray-900 text-white rounded-full px-5 py-2 text-sm font-semibold"
        >
          Message
        </button>
      </div>
    </>
  )}
</div>

          </div>
          );
        })}


    {filteredMatches.length === 0 && (
      <div className="text-center py-12">
        <h3 className="text-lg font-semibold text-gray-500">
          No {matchStatus} matches
        </h3>
        <p className="text-gray-400 mt-2">
          {matchStatus === 'pending'
            ? 'Invites you receive will appear here.'
            : matchStatus === 'completed'
              ? 'Past matches will appear here.'
              : 'Confirmed upcoming matches will show here.'}
        </p>
      </div>
    )}
    {chat.open && (
      <ChatPanel
        user={user}
        otherProfileName = {otherProfileName}
        conversationId={chat.id}
        onClose={() => setChat({ open: false, id: null })}
      />
    )}
  </div>
</div>
  );
};
export default MatchesView;